import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { setCsrfToken, setUnauthorizedHandler } from '../lib/api'

const AuthContext = createContext(null)
export const useAuth = () => useContext(AuthContext)

// Escada de perfis: admin pode tudo o que developer pode, e assim por diante. É a
// mesma ordem do backend (authz.go); aqui serve só para esconder o que não adianta
// mostrar. Quem decide é sempre o servidor.
const ORDER = { product: 1, engineer: 2, developer: 3, admin: 4 }

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [degraded, setDegraded] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me')
      if (res.ok) {
        const me = await res.json()
        setCsrfToken(me.csrf)
        setUser(me)
        setDegraded(false)
        return me
      }
      // Só 401/403 significam "não é você". Um 502 durante um deploy do backend não
      // pode jogar o despachante na tela de login com a sessão ainda válida.
      if (res.status >= 500) {
        setDegraded(true)
        return null
      }
    } catch {
      setDegraded(true)
      return null
    }
    setCsrfToken(null)
    setUser(null)
    return null
  }, [])

  useEffect(() => {
    // O 401 de qualquer chamada derruba o estado local, o que desmonta a árvore
    // autenticada e fecha o WebSocket junto (ver o gate em App.jsx).
    setUnauthorizedHandler(() => {
      setCsrfToken(null)
      setUser(null)
    })
    let alive = true
    ;(async () => {
      const [cfg] = await Promise.all([
        fetch('/api/auth/config').then(r => (r.ok ? r.json() : null)).catch(() => null),
        refresh(),
      ])
      if (!alive) return
      setConfig(cfg)
      setLoading(false)
    })()
    return () => { alive = false }
  }, [refresh])

  const loginLocal = useCallback(async (email, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
    if (!res.ok) {
      // Erro identificado por CÓDIGO, não pela frase do servidor: a mensagem crua vinha
      // em português para um produto que roda em 5 idiomas, e furava a política de não
      // dizer se a conta existe.
      const err = new Error('login_failed')
      err.code = res.status === 403 ? 'origin' : 'invalid_credentials'
      throw err
    }
    return refresh()
  }, [refresh])

  const loginGoogle = useCallback(() => {
    // Navegação de página inteira, não fetch: o fluxo é redirect e quem conduz é o
    // backend (padrão BFF). O SPA nunca vê token do Google.
    window.location.href = '/api/auth/google/start?next=' +
      encodeURIComponent(window.location.pathname + window.location.search)
  }, [])

  const logout = useCallback(async () => {
    // Só limpa o estado local se o servidor confirmou. Limpar sem confirmação leva a
    // UI para o login com a SESSÃO AINDA VIVA no servidor e o cookie no browser: numa
    // estação de despacho compartilhada, o próximo a sentar recarrega e entra na conta
    // do anterior.
    const res = await fetch('/api/auth/logout', { method: 'POST' }).catch(() => null)
    if (!res || !res.ok) {
      const err = new Error('logout_failed')
      err.code = 'logout'
      throw err
    }
    setCsrfToken(null)
    setUser(null)
  }, [])

  const can = useCallback(
    (min) => !!user && (ORDER[user.role] || 0) >= (ORDER[min] || 99),
    [user]
  )

  return (
    <AuthContext.Provider value={{ user, config, loading, degraded, can, loginLocal, loginGoogle, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}
